import { useEffect } from 'react';
import { LiaBrainSolid, LiaInfoCircleSolid, LiaRedoAltSolid, LiaTimesSolid } from 'react-icons/lia';

import { Button } from '@/components-new/button';
import { Drawer, DrawerBody, DrawerHeader, DrawerTitle } from '@/components-new/drawer';
import { EmptyState, EmptyStateBody, EmptyStateHeading } from '@/components-new/empty-state';
import { Loader } from '@/components-new/loader';
import { Overlay } from '@/components-new/overlay';
import { SectionHeading } from '@/components-new/section';
import { useDrugBriefMutation } from '@/features/ai/api';
import { useDelayedClose } from '@/hooks/use-delayed-close';
import { markdownSerializer } from '@/shared/markdown/markdown-serialization';

type DrugBriefDrawerProps = {
  drugId: number;
  drugName: string;
  open: boolean;
  onClose: () => void;
};

export const DrugBriefDrawer = ({ drugId, drugName, open, onClose }: DrugBriefDrawerProps) => {
  const { isOpen, handleOpen, handleClose } = useDelayedClose(open, onClose);
  const { mutate, data, isPending, isError, reset } = useDrugBriefMutation();

  useEffect(() => {
    if (open) {
      handleOpen();
      reset();
      mutate({ drugId });
    }
  }, [open, drugId]);

  const handleRetry = () => {
    reset();
    mutate({ drugId });
  };

  return (
    <Drawer open={isOpen} onClose={handleClose} size="4xl">
      <DrawerHeader>
        <DrawerTitle>
          <div className="flex items-center gap-2">
            <LiaBrainSolid className="size-6 text-secondary-600" />
            Artia Intelligence Brief
          </div>
        </DrawerTitle>
        <Button plain onClick={handleClose}>
          <LiaTimesSolid />
        </Button>
      </DrawerHeader>
      <DrawerBody>
        {isPending && (
          <Overlay>
            <Loader message="Generating your Artia Intelligence brief..." />
          </Overlay>
        )}

        {isError && (
          <EmptyState>
            <LiaBrainSolid className="size-12 text-gray-500" />
            <EmptyStateHeading>Unable to generate brief</EmptyStateHeading>
            <EmptyStateBody>
              Something went wrong generating your Artia Intelligence brief. Please try again.
            </EmptyStateBody>
            <Button color="secondary" onClick={handleRetry}>
              <LiaRedoAltSolid />
              Retry
            </Button>
          </EmptyState>
        )}

        {data && (
          <div className="space-y-6">
            <div>
              <SectionHeading level={3}>{drugName}</SectionHeading>
              <p className="mt-1 text-xs text-gray-500">
                Generated {new Date(data.generatedAt).toLocaleDateString()}
              </p>
            </div>
            <div>
              <SectionHeading level={3}>Artia Intelligence Brief</SectionHeading>
              <div
                className="prose prose-sm mt-3 max-w-none"
                dangerouslySetInnerHTML={{
                  __html: markdownSerializer.deserialize(data.brief),
                }}
              />
            </div>
            <p className="flex items-center gap-1.5 border-t border-gray-200 pt-4 text-xs text-gray-500">
              <LiaInfoCircleSolid className="size-4 shrink-0" />
              Artia Intelligence can make mistakes. Verify important information before making decisions.
            </p>
          </div>
        )}
      </DrawerBody>
    </Drawer>
  );
};
